import { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import Footer from './components/Footer';
import FOMOBanner from './components/FOMOBanner';
import Preloader from './components/Preloader';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ProductsPage from './pages/ProductsPage';
import DistributionPage from './pages/DistributionPage';
import ContactPage from './pages/ContactPage';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import WebsiteCustomizer from './pages/admin/WebsiteCustomizer';
import './App.css';

// Layout component to handle conditional rendering of Header/Footer
const Layout = ({ children }) => {
  const location = useLocation();
  // Check if current path starts with /admin
  const isAdminRoute = location.pathname.startsWith('/admin');

  // If it's an admin route, render content without public header/footer
  if (isAdminRoute) {
    return <div className="min-h-screen bg-gray-50">{children}</div>;
  }

  // Otherwise render with public layout
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>{children}</main>
      <Footer />
      <FOMOBanner />
    </div>
  );
};

function AppContent() {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.body.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  // Create coffee aroma effect
  useEffect(() => {
    const createAromaEffect = () => {
      const aromaContainer = document.createElement('div');
      aromaContainer.className = 'coffee-aroma';
      document.body.appendChild(aromaContainer);

      const createParticle = () => {
        const particle = document.createElement('div');
        particle.className = 'aroma-particle';
        
        // Random position
        const posX = Math.random() * 100;
        const posY = Math.random() * 100;
        const size = Math.random() * 20 + 5;
        const delay = Math.random() * 5;
        
        particle.style.left = `${posX}%`;
        particle.style.top = `${posY}%`;
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.animationDelay = `${delay}s`;
        
        aromaContainer.appendChild(particle);
        
        // Remove particle after animation completes
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
        }, 15000);
      };

      // Create particles periodically
      const interval = setInterval(createParticle, 300);
      
      // Clean up
      return () => {
        clearInterval(interval);
        if (aromaContainer.parentNode) {
          aromaContainer.parentNode.removeChild(aromaContainer);
        }
      };
    };

    // Only create aroma effect on desktop
    if (window.innerWidth > 768) {
      const cleanup = createAromaEffect();
      return cleanup;
    }
  }, []);

  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/distribution" element={<DistributionPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/customizer" element={<WebsiteCustomizer />} />
      </Routes>
    </Layout>
  );
}

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000); 

    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      <AnimatePresence>
        {isLoading && <Preloader />}
      </AnimatePresence>
      {!isLoading && <AppContent />}
    </Router>
  );
}

export default App;